import {
  Box,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  Select,
  FormControl,
  MenuItem,
} from '@mui/material';
import {
  BarChartRounded,
  RefreshRounded,
  SchoolRounded,
  AccountBalanceRounded,
  EventNoteRounded,
  FileDownloadRounded,
  GroupsRounded,
  EventSeatRounded,
  FactCheckRounded,
  PersonSearchRounded,
  WorkspacePremiumRounded,
  InsightsRounded,
} from '@mui/icons-material';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '../../stores/ui.store';
import { CohortTab } from './components/CohortTab';
import { CapacityTab } from './components/CapacityTab';
import { StudentFollowUpTab } from './components/StudentFollowUpTab';
import { TeachingCoverageTab } from './components/TeachingCoverageTab';
import { AcademicTab } from './components/reports/AcademicTab';
import { FinancialTab } from './components/reports/FinancialTab';
import { ExportTab } from './components/reports/ExportTab';
import { MonthlyBreakdownDialog } from './components/reports/MonthlyBreakdownDialog';
import { SetupBanner } from '../../components/setup/SetupBanner';
import {
  ANALYTICS_TAB,
  CANDIDATES_TAB,
  HEAVY_REPORT_TABS,
  reportsInitialTab,
  useReports,
} from '../../hooks/use-reports';
import { usePermissions } from '../../hooks/use-permissions';
import { useTenantConfig } from '../../hooks/use-tenant-config';
import { GraduationCandidatesTab } from './components/reports/GraduationCandidatesTab';
import { showCandidatesTab } from './components/reports/graduation-candidates.model';
import { AnalyticsTab } from './components/analytics/AnalyticsTab';

/**
 * Pestañas cuyo contenido depende del PERÍODO. Las otras dos —Cohortes y
 * Capacidad— se filtran por programa, así que el selector de período no les
 * aplica y no se muestra cuando están activas.
 */
const TERM_SCOPED_TABS = new Set([0, 1, 2, 5]); // Académico, Financiero, Exportar, Cobertura

export function ReportsPage() {
  const { t } = useTranslation('sis');
  const { colorMode } = useUiStore();
  // `graduation_audit`: una pestaña más AL FINAL (no corre a las demás), solo
  // para tenantAdmin y registro. Sin el flag, la página queda como siempre.
  const { isAdmin, hasRole } = usePermissions();
  const tenantConfig = useTenantConfig();
  const credentialRecord = tenantConfig.academicRecord.mode === 'credentials';
  const showCandidates = showCandidatesTab({
    credentialRecord,
    isAdmin,
    isRegistrar: hasRole('registrar'),
  });
  // El panel académico: gestión que entra a /reportes (administración y registro;
  // el decano no entra aquí y finanzas no ve lo académico). Lo exige la función.
  const showAnalytics = isAdmin || hasRole('registrar');
  const isDark = colorMode === 'dark';
  // Abre en Académico para todos (M4·D1).
  const [activeTab, setActiveTab] = useState(reportsInitialTab());
  const {
    kpis,
    academicRows,
    financialRows,
    monthlyBreakdown,
    terms,
    selectedTermId,
    setSelectedTermId,
    loading,
    heavyLoading,
    heavyReady,
    hasStudentsPart,
    error,
    refresh,
  } = useReports({
    // La carga pesada, solo cuando una pestaña la necesita, y solo cuando ya se
    // sabe con qué pestaña abre la página (si no, LABTS la pediría sin usarla).
    heavyEnabled: tenantConfig.settled && HEAVY_REPORT_TABS.has(activeTab),
  });
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  // Academic filters
  const [acadSearch, setAcadSearch] = useState('');
  const [acadEnrollFilter, setAcadEnrollFilter] = useState<'all' | 'enrolled' | 'not_enrolled'>(
    'all',
  );
  const [acadPlanFilter, setAcadPlanFilter] = useState<string>('all');
  const [acadProgramFilter, setAcadProgramFilter] = useState<string>('all');

  const filteredAcademicRows = useMemo(() => {
    const q = acadSearch.toLowerCase();
    return academicRows.filter((r) => {
      const matchSearch =
        !q ||
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.personType.toLowerCase().includes(q) ||
        r.programName.toLowerCase().includes(q);
      const matchEnroll =
        acadEnrollFilter === 'all' ||
        (acadEnrollFilter === 'enrolled' && r.enrolledSections > 0) ||
        (acadEnrollFilter === 'not_enrolled' && r.enrolledSections === 0);
      const matchPlan =
        acadPlanFilter === 'all' ||
        (acadPlanFilter === '__exempt__' ? r.isExempt : false) ||
        (acadPlanFilter === '__no_plan__'
          ? (!r.hasActivePlan || r.planType === '—') && !r.isExempt
          : r.planType === acadPlanFilter);
      const matchProgram = acadProgramFilter === 'all' || r.programName === acadProgramFilter;
      return matchSearch && matchEnroll && matchPlan && matchProgram;
    });
  }, [academicRows, acadSearch, acadEnrollFilter, acadPlanFilter, acadProgramFilter]);

  // Financial filters
  const [finSearch, setFinSearch] = useState('');
  const [finStatusFilter, setFinStatusFilter] = useState<string>('all');
  const [finPlanFilter, setFinPlanFilter] = useState<string>('all');
  const [finProgramFilter, setFinProgramFilter] = useState<string>('all');

  const filteredFinancialRows = useMemo(() => {
    const q = finSearch.toLowerCase();
    return financialRows.filter((r) => {
      const matchSearch =
        !q || r.studentName.toLowerCase().includes(q) || r.programName.toLowerCase().includes(q);
      const matchStatus =
        finStatusFilter === 'all' ||
        (finStatusFilter === '__exempt__' ? r.isExempt : false) ||
        (!r.isExempt && r.status === finStatusFilter);
      const matchPlan =
        finPlanFilter === 'all' ||
        (finPlanFilter === '__no_plan__'
          ? r.planType === '—' || !r.planType
          : r.planType === finPlanFilter);
      const matchProgram = finProgramFilter === 'all' || r.programName === finProgramFilter;
      return matchSearch && matchStatus && matchPlan && matchProgram;
    });
  }, [financialRows, finSearch, finStatusFilter, finPlanFilter, finProgramFilter]);

  const isAcadFilterActive =
    acadSearch !== '' ||
    acadEnrollFilter !== 'all' ||
    acadPlanFilter !== 'all' ||
    acadProgramFilter !== 'all';
  const isFinFilterActive =
    finSearch !== '' ||
    finStatusFilter !== 'all' ||
    finPlanFilter !== 'all' ||
    finProgramFilter !== 'all';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <SetupBanner />
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        {/* Sin bajada: "Visión consolidada de la gestión académica y financiera"
            no cambia nunca y no ayuda a decidir nada. Misma regla que dentro de
            las pestañas — visible sólo lo que cambia con los datos. */}
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box sx={{ color: 'primary.main', display: 'flex' }}>
            <BarChartRounded />
          </Box>
          <Typography variant="h4" fontWeight={800}>
            {t('common:nav.reports')}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1.5}>
          {/* El selector de período aparece SÓLO en las pestañas que lo usan.
              Cohortes y Capacidad filtran por programa y tienen su propio
              selector adentro: dejarlo visible ahí prometía un filtro que no
              se aplicaba, y eso es peor que no ofrecerlo. */}
          {terms.length > 0 && TERM_SCOPED_TABS.has(activeTab) && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={selectedTermId ?? '__all__'}
                onChange={(e) =>
                  setSelectedTermId(e.target.value === '__all__' ? null : e.target.value)
                }
                startAdornment={
                  <Box
                    component={EventNoteRounded}
                    sx={{ fontSize: 16, mr: 0.5, color: 'primary.main' }}
                  />
                }
              >
                <MenuItem value="__all__">{t('reports.allTerms')}</MenuItem>
                {terms.map((term) => (
                  <MenuItem key={term.id} value={term.id}>
                    {term.name}
                    {term.status === 'open' && (
                      <Chip
                        size="small"
                        label={t('reports.activeTerm')}
                        color="success"
                        variant="outlined"
                        sx={{ ml: 1, height: 18, fontSize: 10 }}
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Tooltip title={t('reports.refresh')}>
            <IconButton onClick={refresh} size="small">
              <RefreshRounded />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v as number)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          icon={<SchoolRounded />}
          iconPosition="start"
          label={t('reports.tabs.academic')}
          value={0}
        />
        <Tab
          icon={<AccountBalanceRounded />}
          iconPosition="start"
          label={t('reports.tabs.financial')}
          value={1}
        />
        <Tab
          icon={<FileDownloadRounded />}
          iconPosition="start"
          label={t('reports.tabs.export')}
          value={2}
        />
        <Tab
          icon={<GroupsRounded />}
          iconPosition="start"
          label={t('reports.tabs.cohort')}
          value={3}
        />
        <Tab
          icon={<EventSeatRounded />}
          iconPosition="start"
          label={t('reports.tabs.capacity')}
          value={4}
        />
        <Tab
          icon={<FactCheckRounded />}
          iconPosition="start"
          label={t('reports.tabs.coverage')}
          value={5}
        />
        <Tab
          icon={<PersonSearchRounded />}
          iconPosition="start"
          label={t('reports.tabs.followUp')}
          value={6}
        />
        {showCandidates && (
          <Tab
            icon={<WorkspacePremiumRounded />}
            iconPosition="start"
            label={t('reports.tabs.candidates')}
            value={CANDIDATES_TAB}
          />
        )}
        {showAnalytics && (
          <Tab
            icon={<InsightsRounded />}
            iconPosition="start"
            label={t('reports.tabs.analytics')}
            value={ANALYTICS_TAB}
          />
        )}
      </Tabs>

      {HEAVY_REPORT_TABS.has(activeTab) && (heavyLoading || !heavyReady) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}
      {activeTab === 0 && heavyReady && !hasStudentsPart && (
        <Alert severity="info">{t('reports.studentsNotAvailable')}</Alert>
      )}
      {activeTab === 0 && heavyReady && hasStudentsPart && (
        <AcademicTab
          kpis={kpis}
          rows={academicRows}
          filtered={filteredAcademicRows}
          search={acadSearch}
          setSearch={setAcadSearch}
          enrollFilter={acadEnrollFilter}
          setEnrollFilter={setAcadEnrollFilter}
          planFilter={acadPlanFilter}
          setPlanFilter={setAcadPlanFilter}
          programFilter={acadProgramFilter}
          setProgramFilter={setAcadProgramFilter}
        />
      )}
      {activeTab === 1 && heavyReady && (
        <FinancialTab
          kpis={kpis}
          onOpenBreakdown={() => setBreakdownOpen(true)}
          rows={financialRows}
          filtered={filteredFinancialRows}
          search={finSearch}
          setSearch={setFinSearch}
          statusFilter={finStatusFilter}
          setStatusFilter={setFinStatusFilter}
          planFilter={finPlanFilter}
          setPlanFilter={setFinPlanFilter}
          programFilter={finProgramFilter}
          setProgramFilter={setFinProgramFilter}
        />
      )}
      {activeTab === 2 && heavyReady && (
        <ExportTab
          academicRows={academicRows}
          financialRows={financialRows}
          filteredAcademicRows={filteredAcademicRows}
          filteredFinancialRows={filteredFinancialRows}
          isAcadFilterActive={isAcadFilterActive}
          isFinFilterActive={isFinFilterActive}
        />
      )}
      {activeTab === 3 && <CohortTab />}
      {activeTab === 4 && <CapacityTab />}
      {activeTab === 5 && <TeachingCoverageTab termId={selectedTermId} />}
      {activeTab === 6 && <StudentFollowUpTab termId={selectedTermId} />}
      {activeTab === CANDIDATES_TAB && showCandidates && <GraduationCandidatesTab />}
      {activeTab === ANALYTICS_TAB && showAnalytics && <AnalyticsTab />}

      {/* Monthly Breakdown Dialog */}
      <MonthlyBreakdownDialog
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        rows={monthlyBreakdown}
        totalCommitted={kpis.totalCommitted}
        currency={kpis.currency}
        isDark={isDark}
      />
    </Box>
  );
}
